"use strict";
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C59 · The chart catalogue — 25 ways to draw one report.                   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The product shipped two (`bar`, `pie`); `REP_CHART_GROUPS` in the Phase 4 mockup offers
 * twenty-five in two groups, and the gap was the largest single thing missing from the builder.
 *
 * ⚠️⚠️ **THE DEFINITION DOES NOT CHANGE, AND THAT IS THE DESIGN.** A report is still ONE measure
 * over ONE dimension — the mockup's rail has plural headers and a single value behind each. What
 * the twenty-five do is DRAW that one result differently, plus one thing that looks like a second
 * dimension and is not:
 *
 * ⚠️⚠️ **THE "SERIES" IS THE MEASURE'S NATURAL COMPANION, NOT A SECOND GROUPING.** `repSeries` in
 * the mockup pairs *Rögzített óra* with *Becslés*, and a money measure with *Büdzsé*. That is why a
 * stacked or grouped bar is offerable at all without a second dimension picker — and why `count`
 * cannot have one: a count has nothing to be compared against. Reading the stack as "group by a
 * second field" is the mistake this note exists to prevent.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.HISTOGRAM_BINS = exports.FALLBACK_CHART = exports.CHART_NEEDS = exports.CHART_GROUP_OF = exports.REPORT_CHARTS = exports.CHART_GROUPS = void 0;
exports.companionOf = companionOf;
exports.chartRefusal = chartRefusal;
exports.histogram = histogram;
exports.CHART_GROUPS = ['featured', 'basic'];
/**
 * ⚠️ **The mockup's own order, group by group.** *Kiemelt* leads with the eight that make somebody
 * open the picker; *Alap* holds the workhorses. Sorting these alphabetically would bury the treemap
 * under the table.
 */
exports.REPORT_CHARTS = [
    /* ── Kiemelt ─────────────────────────────────────────────────────────── */
    'treemap',
    'waterfall',
    'calendar',
    'sunburst',
    'bubble',
    'scatter',
    'radar',
    'barline',
    /* ── Alap ────────────────────────────────────────────────────────────── */
    'line',
    'area',
    'vbar',
    'vstack',
    'vgroup',
    'hbar',
    'hstack',
    'hgroup',
    'listmetric',
    'listdim',
    'metric',
    'table',
    'gauge',
    'donut',
    'breakdown',
    'histogram',
    'heatmap',
];
exports.CHART_GROUP_OF = {
    treemap: 'featured',
    waterfall: 'featured',
    calendar: 'featured',
    sunburst: 'featured',
    bubble: 'featured',
    scatter: 'featured',
    radar: 'featured',
    barline: 'featured',
    line: 'basic',
    area: 'basic',
    vbar: 'basic',
    vstack: 'basic',
    vgroup: 'basic',
    hbar: 'basic',
    hstack: 'basic',
    hgroup: 'basic',
    listmetric: 'basic',
    listdim: 'basic',
    metric: 'basic',
    table: 'basic',
    gauge: 'basic',
    donut: 'basic',
    breakdown: 'basic',
    histogram: 'basic',
    heatmap: 'basic',
};
/**
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHAT A CHART NEEDS BEYOND THE ROWS.                              │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * Most of the twenty-five draw from `rows` alone. Three want something the group rows cannot
 * express, and offering them anyway would draw an empty box that looks like a bug:
 *
 * | | |
 * |---|---|
 * | `companion` | a second series to compare against — the measure's own pair |
 * | `days`      | a value per calendar day, which a grouped row has thrown away |
 * | `matrix`    | group × month, which is a second axis the definition does not carry |
 *
 * ⚠️ **The picker DISABLES rather than hides**, which is the mockup's own behaviour: a reader who
 * came looking for a sunburst should find out it needs a companion series, not conclude the product
 * does not have one.
 */
exports.CHART_NEEDS = {
    treemap: null,
    waterfall: null,
    calendar: 'days',
    sunburst: 'companion',
    bubble: null,
    scatter: null,
    radar: 'companion',
    barline: 'companion',
    line: null,
    area: null,
    vbar: null,
    vstack: 'companion',
    vgroup: 'companion',
    hbar: null,
    hstack: 'companion',
    hgroup: 'companion',
    listmetric: null,
    listdim: null,
    metric: null,
    table: null,
    gauge: null,
    donut: null,
    breakdown: null,
    histogram: null,
    heatmap: 'matrix',
};
/**
 * Whether a measure has a companion to compare against.
 *
 * ⚠️⚠️ **`count` HAS NONE, AND THAT IS NOT AN OVERSIGHT.** *Rögzített óra* pairs with *Becslés* and
 * a money figure pairs with its *Büdzsé*, because both answer *"against what"*. A count of entries
 * has nothing standing beside it, so every chart that draws two series is refused — the alternative
 * is a stacked bar with one segment, which is a bar that has learned to lie about its own shape.
 */
function companionOf(metric) {
    if (metric === 'hours' || metric === 'estimate')
        return 'estimate';
    if (metric === 'revenue' || metric === 'cost' || metric === 'margin')
        return 'budget';
    /* ⚠️ **Overtime has none either**, and for the same reason a count does not: the estimate is
       what the WHOLE task was expected to take, which is not what an overtime hour is measured
       against. Pairing them would draw a comparison nobody asked a question about. */
    return null;
}
/**
 * Why a chart cannot be drawn for this report, or `null` when it can.
 *
 * ⚠️ Returns the REASON rather than a boolean, so the picker can say which of the three it is —
 * *"needs a measure with something to compare against"* is actionable, *"unavailable"* is not.
 */
function chartRefusal(chart, report) {
    const needs = exports.CHART_NEEDS[chart];
    if (needs === null)
        return null;
    if (needs === 'companion')
        return companionOf(report.metric) === null ? 'companion' : null;
    if (needs === 'days')
        return report.hasDays ? null : 'days';
    return report.hasMatrix ? null : 'matrix';
}
/**
 * ⚠️ **Where a chart falls back to when its report can no longer carry it.** Switching the measure
 * to `count` with a radar on screen must not leave a blank panel; the mockup's own `setRepAgg`
 * demotes in the same spirit, and `vbar` is the one shape every report can draw.
 */
exports.FALLBACK_CHART = 'vbar';
/**
 * ⚠️⚠️ **THE FIVE BINS A HISTOGRAM USES, AND IT BINS THE GROUPS — NOT THE RECORDS.** *"How many
 * projects booked between 40 and 80 hours"* is the question a report with a project dimension can
 * answer; *"how many worklogs were between 1 and 2 hours"* is a different report over rows this one
 * has already summed away. The mockup bins `d.map(r => r.v)` for the same reason.
 */
exports.HISTOGRAM_BINS = 5;
function histogram(values, bins = exports.HISTOGRAM_BINS) {
    if (values.length === 0)
        return [];
    const magnitudes = values.map((one) => Math.abs(one));
    const low = Math.min(...magnitudes);
    const high = Math.max(...magnitudes);
    /* ⚠️ A flat set has no range to divide — one bin of everything beats dividing by zero. */
    const step = (high - low) / bins || 1;
    const out = Array.from({ length: bins }, (_, index) => ({
        from: low + index * step,
        to: low + (index + 1) * step,
        count: 0,
    }));
    for (const value of magnitudes) {
        const at = Math.max(0, Math.min(bins - 1, Math.floor((value - low) / step)));
        const bin = out[at];
        if (bin !== undefined)
            bin.count += 1;
    }
    return out;
}
//# sourceMappingURL=charts.js.map